import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { useTheme } from '../../ThemeContext';

/**
 * OverflowMenu - Reusable 3-dot actions menu with themed styling
 *
 * Props:
 * - items: Array<
 *    | { icon?: React.ReactNode, label: string, onClick: Function, disabled?: boolean, danger?: boolean }
 *    | { type: 'divider' }
 *   >
 * - ariaLabel?: string
 * - dotSize?: number
 * - dotColor?: string (overrides theme color)
 * - minWidth?: number
 */
const OverflowMenu = ({
  items = [],
  ariaLabel = 'Actions',
  dotSize = 22,
  dotColor,
  minWidth = 300,
}) => {
  const { themeMode, currentTheme } = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const triggerColor = dotColor || (themeMode === 'dark' ? '#ffffff' : '#2e2e2e');

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpen}
        aria-label={ariaLabel}
        sx={{
          width: 36,
          height: 36,
        }}
      >
        <span style={{ fontSize: dotSize, lineHeight: 1, color: triggerColor }}>⋮</span>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            backgroundColor: themeMode === 'dark' ? '#0b073a' : '#ffffff',
            color: currentTheme.homePage.textColor,
            border: themeMode === 'dark' ? '1px solid #302d4c' : '1px solid #e9e8ea',
            borderRadius: '14px',
            minWidth,
            boxShadow: themeMode === 'dark'
              ? '0 16px 32px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255,255,255,0.06)'
              : '0 16px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            py: 0.5,
          },
        }}
      >
        {items.map((item, idx) => {
          if (item && item.type === 'divider') {
            return (
              <Divider key={`div-${idx}`} sx={{ my: 0.5, borderColor: themeMode === 'dark' ? '#302d4c' : '#e9e8ea' }} />
            );
          }

          const { icon, label, onClick, disabled, danger } = item || {};
          return (
            <MenuItem
              key={`mi-${idx}`}
              disabled={disabled}
              onClick={() => {
                handleClose();
                if (onClick) onClick();
              }}
              sx={{
                py: 1.25,
                px: 1.5,
                gap: 1,
              }}
            >
              {icon ? (
                <ListItemIcon sx={{ minWidth: 28 }}>
                  {icon}
                </ListItemIcon>
              ) : null}
              <ListItemText
                primary={
                  <span style={{ color: danger ? '#e63e2b' : undefined }}>{label}</span>
                }
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default OverflowMenu;

